import {initialState,demoUser} from './mockData.js';
import {localDb,confidentialCollections} from './localDb.js';
import {hydrateWorkspaceFromPb} from './pbWorkspace.js';
import {authRefresh,pbSessionUser,signOut as pbSignOut,pb} from '../services/pb.js';
import {indexAdd, indexUpdate, indexRemove, initSearchIndex, SEARCHABLE_COLLECTIONS} from '../services/search.js';

const STORAGE_KEY='command-center-system-mock-v2';
const clone=(value)=> JSON.parse(JSON.stringify(value));
const wait=(ms=120)=> new Promise((resolve)=> setTimeout(resolve,ms));
const listeners=new Set();

const confidentialStateMap={
  chatSessions: 'chatSessions',
  chatMessages: 'messages',
  chatSummaries: 'chatSummaries',
  botConsultationLogs: 'botConsultationLogs',
  plans: 'plans',
  tasks: 'tasks',
  files: 'files',
  inboxMessages: 'inbox',
  adCampaignBriefs: 'adCampaignBriefs',
  businessMemory: 'businessMemory',
  agentFlows: 'agentFlows',
  knowledgeSources: 'knowledgeSources',
  llmProviderCredentials: 'providerCredentials',
  // Wave A (T10): projects is a Dexie-confidential collection (dexie.schema v3).
  // Without this entry create()/hydrate() never sync state.projects — projects
  // created in the UI would vanish from the workspace on reload.
  projects: 'projects'
};

const singletonCollections=new Set(['business','brandProfile']);

// Collection-name → state-key aliases. Screens and services use the canonical
// PB collection names (e.g. 'usageLogs'); the mock state stores them under
// legacy keys (systemStorage maps state key → storage key). Without this,
// create('usageLogs') writes an orphan state.usageLogs that no screen reads
// and persistSystem never saves — AO usage rows silently vanish.
const systemCollectionStateKey={
  usageLogs: 'usage'
};

function systemStateKey(collection) {
  return systemCollectionStateKey[collection] || collection;
}

let state={
  ...clone(initialState),
  businessMembers: [],
  chatSessions: [],
  chatSummaries: [],
  botConsultationLogs: [],
  businessMemory: [],
  agentFlows: [],
  knowledgeSources: [],
  providerCredentials: [],
  projects: [],
  subscriptionPlans: [
    {
      id: 'subscription_starter',
      slug: 'starter',
      displayName: 'Starter',
      monthlyAoAllowance: 2000,
      memoryRetention: 'seven_day',
      price: 29
    },
    {
      id: 'subscription_growth',
      slug: 'growth',
      displayName: 'Growth',
      monthlyAoAllowance: 10000,
      memoryRetention: 'persistent',
      price: 99
    },
    {
      id: 'subscription_scale',
      slug: 'scale',
      displayName: 'Scale',
      monthlyAoAllowance: 25000,
      memoryRetention: 'persistent',
      price: 249
    }
  ]
};

// Fix #1: when true the workspace is hydrated from PocketBase and system
// collections are PB-authoritative — mock localStorage rows must NOT be
// persisted (they would be re-read as a stale source of truth on next boot).
// In-session edits to shared collections still update the in-memory state so
// screens respond immediately; PB write-through behind dataClient is the
// documented follow-up (docs/deploy_fixes.md #1).
let pbWorkspaceActive=false;

// Re-apply a hydrated PB workspace patch to the in-memory state (the same
// keys initSession replaces on login). Shared collection write paths
// (rosterTransfer) call dataClient.refreshWorkspace() after PB writes so the
// UI reflects the server's authoritative rows — ids and relations included.
function applyWorkspacePatch(patch) {
  state.session = patch.session;
  state.business = patch.business;
  state.brandProfile = patch.brandProfile;
  state.modules = patch.modules;
  state.modelTiers = patch.modelTiers;
  state.teams = patch.teams;
  state.bots = patch.bots;
  state.subscriptionPlans = patch.subscriptionPlans;
  notify();
}

const systemStorage={
  business: 'business',
  brandProfile: 'brandProfile',
  modules: 'modules',
  teams: 'teams',
  bots: 'bots',
  modelTiers: 'modelTiers',
  marketplace: 'marketplace',
  usage: 'usageLogs',
  activity: 'auditLogs',
  websitePages: 'sitePages'
};

function notify() {
  for (const listener of listeners) listener(clone(state));
}

function persistSystem() {
  if (pbWorkspaceActive) return; // PB is authoritative — never write mock rows
  const systemState={};

  for (const [source,target] of Object.entries(systemStorage)) {
    systemState[target]=state[source];
  }

  localStorage.setItem(STORAGE_KEY,JSON.stringify(systemState));
}

async function seedDexie() {
  for (const [collection,source] of Object.entries(confidentialStateMap)) {
    const existing=await localDb.table(collection).count();

    if (existing > 0) continue;

    const records=collection==='chatMessages'
      ? Object.entries(state.messages || {}).flatMap(([module,rows])=> (
        rows.map((row)=> ({...row,module}))
      ))
      : state[source] || [];

    if (records.length) {
      await localDb.table(collection).bulkPut(records);
    }
  }
}

async function hydrate() {
  try {
    const saved=JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

    for (const [source,target] of Object.entries(systemStorage)) {
      if (saved[target] !==undefined) state[source]=saved[target];
    }

    await seedDexie();

    for (const [collection,source] of Object.entries(confidentialStateMap)) {
      const records=await localDb.table(collection).toArray();

      if (collection==='chatMessages') {
        state.messages={};

        records.forEach((record)=> {
          state.messages[record.module]=[
            ...(state.messages[record.module] || []),
            record
          ];
        });
      } else {
        state[source]=records;
      }
    }

    notify();
    initSearchIndex(state);
  } catch {
    notify();
  }
}

hydrate();

function systemCollection(collection) {
  return !confidentialCollections.includes(collection);
}

function getRows(collection) {
  const source=confidentialStateMap[collection];
  return state[source] || state[systemStateKey(collection)] || [];
}

export const dataClient={
  peek() {
    return clone(state);
  },

  async getState() {
    await wait();
    return clone(state);
  },

  subscribe(listener) {
    listeners.add(listener);
    return ()=> listeners.delete(listener);
  },

  async list(collection) {
    await wait();

    if (systemCollection(collection)) {
      const value=state[systemStateKey(collection)];
      return clone(Array.isArray(value) ? value : []);
    }

    return clone(await localDb.table(collection).toArray());
  },

  async find(collection,predicate) {
    const rows=await this.list(collection);
    return rows.find(predicate);
  },

  async create(collection,record) {
    await wait();

    const created={
      id: record.id || `${collection}_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      created: new Date().toISOString(),
      ...record
    };

    const key=systemStateKey(collection);

    if (systemCollection(collection)) {
      if (singletonCollections.has(collection)) {
        state[key]={
          ...(state[key] || {}),
          ...created
        };
      } else {
        state[key]=[
          ...(Array.isArray(state[key]) ? state[key] : []),
          created
        ];
      }

      persistSystem();
    } else {
      await localDb.table(collection).put(created);
      const source=confidentialStateMap[collection];

      if (collection==='chatMessages') {
        state.messages[created.module]=[
          ...(state.messages[created.module] || []),
          created
        ];
      } else {
        state[source]=[
          ...(Array.isArray(state[source]) ? state[source] : []),
          created
        ];
      }
    }

    indexAdd(collection, created);

    notify();
    return clone(created);
  },

  async update(collection,id,patch) {
    await wait();

    const key=systemStateKey(collection);

    if (systemCollection(collection)) {
      if (singletonCollections.has(collection)) {
        state[key]={
          ...(state[key] || {}),
          ...patch
        };
      } else {
        const current=Array.isArray(state[key])
          ? state[key]
          : [];

        state[key]=current.map((item)=> (
          item.id===id ? {...item,...patch} : item
        ));
      }

      persistSystem();
    } else {
      await localDb.table(collection).update(id,patch);
      const source=confidentialStateMap[collection];

      if (collection==='chatMessages') {
        Object.keys(state.messages || {}).forEach((module)=> {
          state.messages[module]=(state.messages[module] || []).map((item)=> (
            item.id===id ? {...item,...patch} : item
          ));
        });
      } else {
        state[source]=(state[source] || []).map((item)=> (
          item.id===id ? {...item,...patch} : item
        ));
      }
    }

    if (SEARCHABLE_COLLECTIONS.includes(collection)) {
      const rows=getRows(collection);
      const updatedRec=Array.isArray(rows) ? rows.find((item)=> item.id===id) : null;
      if (updatedRec) indexUpdate(collection, updatedRec);
    }

    notify();

    if (singletonCollections.has(collection)) {
      return clone(state[systemStateKey(collection)]);
    }

    return clone(getRows(collection).find((item)=> item.id===id));
  },

  async remove(collection,id) {
    await wait();

    if (systemCollection(collection)) {
      if (singletonCollections.has(collection)) {
        throw new Error(`${collection} is a singleton and cannot be removed.`);
      }

      const key=systemStateKey(collection);
      state[key]=(state[key] || []).filter((item)=> item.id!==id);
      persistSystem();
    } else {
      await localDb.table(collection).delete(id);
      const source=confidentialStateMap[collection];

      if (collection==='chatMessages') {
        Object.keys(state.messages || {}).forEach((module)=> {
          state.messages[module]=(state.messages[module] || []).filter(
            (item)=> item.id!==id
          );
        });
      } else {
        state[source]=(state[source] || []).filter((item)=> item.id!==id);
      }
    }

    indexRemove(collection, id);

    notify();
    return {removed: true,id};
  },

  async setBusiness(business,brandProfile) {
    state.business={...state.business,...business};
    state.brandProfile={...state.brandProfile,...brandProfile};
    persistSystem();
    notify();
    return clone(state.business);
  },

  async addMessage(module,message) {
    return this.create('chatMessages',{...message,module});
  },

  async installTemplate(templateId) {
    state.installedTemplates=[
      ...(state.installedTemplates || []),
      templateId
    ];
    persistSystem();
    notify();
    return {installed: true,templateId};
  },

  // ── Fix #1 (docs/deploy_fixes.md): PB-backed session + workspace ──────────
  //
  // On app boot (and after login) this validates the persisted PB token and,
  // when valid, hydrates the tenant's OWN business data from PocketBase —
  // replacing the mock "Northstar" demo in shared/system collections. Dexie
  // confidential tables are untouched by hydration (owner invariant); they are
  // per-business only once the demo seed is dropped for PB sessions (below).
  // Returns {authenticated, workspace: 'pb'|'demo'|'none'}.
  async initSession() {
    const valid = await authRefresh();
    if (!valid) {
      state.session = { user: null, authenticated: false };
      notify();
      return { authenticated: false, workspace: 'none' };
    }
    try {
      const patch = await hydrateWorkspaceFromPb();
      if (patch) {
        pbWorkspaceActive = true;
        // Replace system/workspace state with the tenant's PB rows. Business/
        // brand/membership/session come from PB; installedTemplates/marketplace
        // stay local (they are client UI state, not PB collections).
        applyWorkspacePatch(patch);
        localStorage.removeItem(STORAGE_KEY); // mock system rows no longer authoritative
        return { authenticated: true, workspace: 'pb' };
      }
      state.session = pbSessionUser()
        ? { user: pbSessionUser(), authenticated: true }
        : { user: null, authenticated: false };
      notify();
      return { authenticated: Boolean(pbSessionUser()), workspace: 'none' };
    } catch (err) {
      console.error('PB workspace hydration failed:', err?.message || err);
      // Keep the demo/local session only in dev; in production a failed
      // hydration must not silently fall back to demo data.
      if (import.meta.env?.DEV) {
        notify();
        return { authenticated: true, workspace: 'demo' };
      }
      state.session = { user: null, authenticated: false };
      notify();
      return { authenticated: false, workspace: 'none' };
    }
  },

  /** Logout: clear the PB token + the in-memory session (frontend.md §8.2). */
  signOut() {
    pbWorkspaceActive = false;
    pbSignOut();
    state.session = { user: null, authenticated: false };
    notify();
  },

  /** True when shared collections are PB-authoritative for this session. */
  isPbWorkspace() {
    return pbWorkspaceActive;
  },

  /**
   * Re-hydrate the workspace from PocketBase after shared-collection writes
   * (roster import). PB is the source of truth; ids/relations come back as the
   * server stored them. No-op (returns false) outside a PB workspace — the
   * demo/local path persists through dataClient.create/update instead.
   */
  async refreshWorkspace() {
    if (!pbWorkspaceActive) return false;
    const patch = await hydrateWorkspaceFromPb();
    if (!patch) return false;
    applyWorkspacePatch(patch);
    return true;
  },

  /** Dev-only demo session (?demo=1 / localStorage command-center-demo=1). */
  enterDemo() {
    state.session = { user: demoUser, authenticated: true };
    notify();
  },

  /** Subscribe to PB auth-store changes (token set/cleared/refreshed). */
  onAuthChange(listener) {
    return pb.authStore.onChange(() => listener({ authenticated: pb.authStore.isValid }));
  }
};

// BB-1 (docs/build_bot_platform.md §4): plan-aware bot-count display. This is a
// SOFT, informational surface only — the control point stays AO metering
// (§3.4 explicitly rejects hard max_bots gates). Slugs cover both the client
// seed (starter/growth/scale) and the backend.md §5 PB seed (free/starter/scale).
// When subscription_plan eventually grows a real field, this map collapses into it.
const BOT_SOFT_LIMITS={
  free: 5,
  starter: 10,
  growth: 25,
  scale: 100
};
const BOT_SOFT_LIMIT_DEFAULT=10;

export function getBotSoftLimit(snapshot=state) {
  const plan=(snapshot.subscriptionPlans || []).find(
    (item)=> item.id===snapshot.business?.subscriptionPlanId
  );

  return {
    limit: BOT_SOFT_LIMITS[plan?.slug] || BOT_SOFT_LIMIT_DEFAULT,
    planName: plan?.displayName || 'Current plan',
    used: (snapshot.bots || []).length
  };
}